import { Gamepad2, Lightbulb, Server } from "lucide-react"
import { useEffect, useLayoutEffect, useRef, useState } from "react"

import { cn } from "@/lib/utils"
import { usePreferences } from "@/preferences"

const cardzoneCards = ["PUBG · 325 UC", "Apple iTunes", "Yalla Ludo", "PUBG · 660 UC"]
const cardzoneScenarios = [
  [0, 1, 2],
  [2, 0, 1],
  [1, 2, 0],
  [2, 0],
  [0, 1],
]
const providerY = [65, 150, 235]

type ProviderState = "idle" | "trying" | "failed" | "success"

const wait = (duration: number, timers: number[]) =>
  new Promise<void>((resolve) => {
    timers.push(window.setTimeout(resolve, duration))
  })

export function CardzoneRoutingPreview() {
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
  const holdRef = useRef<HTMLDivElement>(null)
  const [stageScale, setStageScale] = useState(1)
  const [cardIndex, setCardIndex] = useState(0)
  const [providerStates, setProviderStates] = useState<ProviderState[]>([
    reducedMotion ? "failed" : "idle",
    reducedMotion ? "failed" : "idle",
    reducedMotion ? "success" : "idle",
  ])
  const [lineStates, setLineStates] = useState<ProviderState[]>([
    reducedMotion ? "failed" : "idle",
    reducedMotion ? "failed" : "idle",
    reducedMotion ? "success" : "idle",
  ])
  const [orderState, setOrderState] = useState<"idle" | "routing" | "success">(
    reducedMotion ? "success" : "idle",
  )
  const [packet, setPacket] = useState({ x: 92, y: 150, visible: false })

  useLayoutEffect(() => {
    const hold = holdRef.current
    if (!hold) return

    const updateScale = () => {
      const width = hold.getBoundingClientRect().width
      if (width > 0) setStageScale(width / 620)
    }

    updateScale()
    if (!("ResizeObserver" in window)) return

    const observer = new ResizeObserver(updateScale)
    observer.observe(hold)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (reducedMotion) return

    const timers: number[] = []
    let cancelled = false

    const run = async () => {
      let cycle = 0

      while (!cancelled) {
        const scenario = cardzoneScenarios[cycle % cardzoneScenarios.length]
        setCardIndex(cycle % cardzoneCards.length)
        setProviderStates(["idle", "idle", "idle"])
        setLineStates(["idle", "idle", "idle"])
        setOrderState("routing")
        setPacket({ x: 92, y: 150, visible: true })
        await wait(80, timers)
        if (cancelled) return

        setPacket({ x: 310, y: 150, visible: true })
        await wait(680, timers)

        for (let index = 0; index < scenario.length; index += 1) {
          if (cancelled) return
          const provider = scenario[index]
          const winner = index === scenario.length - 1

          setProviderStates((current) =>
            current.map((state, providerIndex) =>
              providerIndex === provider ? "trying" : state,
            ),
          )
          setLineStates((current) =>
            current.map((state, providerIndex) =>
              providerIndex === provider ? "trying" : state,
            ),
          )
          setPacket({ x: 520, y: providerY[provider], visible: true })
          await wait(660, timers)
          if (cancelled) return

          setProviderStates((current) =>
            current.map((state, providerIndex) =>
              providerIndex === provider ? (winner ? "success" : "failed") : state,
            ),
          )
          setLineStates((current) =>
            current.map((state, providerIndex) =>
              providerIndex === provider ? (winner ? "success" : "failed") : state,
            ),
          )

          if (!winner) {
            setPacket({ x: 310, y: 150, visible: false })
            await wait(330, timers)
            setPacket({ x: 310, y: 150, visible: true })
          }
        }

        if (cancelled) return
        await wait(420, timers)
        setPacket({ x: 310, y: 150, visible: true })
        await wait(180, timers)
        setPacket({ x: 92, y: 150, visible: true })
        await wait(680, timers)
        if (cancelled) return
        setOrderState("success")
        setPacket((current) => ({ ...current, visible: false }))
        await wait(1_700, timers)
        cycle += 1
      }
    }

    void run()

    return () => {
      cancelled = true
      timers.forEach((timer) => window.clearTimeout(timer))
    }
  }, [reducedMotion])

  return (
    <div className="cardzone-routing" dir="ltr" aria-label="Cardzone provider routing demo">
      <div className="cardzone-routing__hold" ref={holdRef}>
        <div className="cardzone-routing__stage" style={{ transform: `scale(${stageScale})` }}>
          <svg className="cardzone-routing__lines" viewBox="0 0 620 300" aria-hidden="true">
            <path className={cn("routing-line routing-line--order", orderState !== "idle" && "is-on")} d="M92 150 H310" />
            <path className={cn("routing-line", lineStates[0] !== "idle" && `is-${lineStates[0]}`)} d="M310 150 C390 150 421 65 520 65" />
            <path className={cn("routing-line", lineStates[1] !== "idle" && `is-${lineStates[1]}`)} d="M310 150 H520" />
            <path className={cn("routing-line", lineStates[2] !== "idle" && `is-${lineStates[2]}`)} d="M310 150 C390 150 421 235 520 235" />
          </svg>

          <span
            className={cn("routing-trail", packet.visible && "is-visible")}
            style={{ left: packet.x, top: packet.y }}
          />
          <span
            className={cn("routing-packet", packet.visible && "is-visible")}
            style={{ left: packet.x, top: packet.y }}
          />

          <div className={cn("routing-node routing-node--order", orderState === "success" && "is-success")} style={{ left: 92, top: 150 }}>
            <span className="routing-node__ripple" />
            <Gamepad2 className="routing-node__icon" aria-hidden="true" />
            <span className="routing-node__text">
              <strong>{cardzoneCards[cardIndex]}</strong>
              <small>Order</small>
            </span>
            <span className="routing-node__check" aria-hidden="true">✓</span>
          </div>

          <div className="routing-node routing-node--router" style={{ left: 310, top: 150 }}>
            <strong>Cardzone</strong>
          </div>

          {providerStates.map((state, index) => (
            <div
              className={cn("routing-node routing-node--provider", state !== "idle" && `is-${state}`)}
              style={{ left: 520, top: providerY[index] }}
              key={index}
            >
              <span className="routing-node__status" aria-hidden="true">
                {state === "failed" ? "✕" : state === "success" ? "✓" : <i />}
              </span>
              <Server className="routing-node__icon" aria-hidden="true" />
              <strong>Provider {String.fromCharCode(65 + index)}</strong>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

type ChurnConversation = {
  question: string
  response: string
  type: "churn"
  customers: { name: string; value: string; risk: number }[]
  total: string
  advice: string
}

type CodConversation = {
  question: string
  response: string
  type: "cod"
  alert: string
  subtitle: string
  lost: string
  recoverable: string
  advice: string
}

type DashboardConversation = {
  question: string
  response: string
  type: "dashboard"
  period: string
  badge: string
  sales: string
  salesLabel: string
  metrics: { label: string; value: string; change: string }[]
}

type Conversation = ChurnConversation | CodConversation | DashboardConversation

const ibnMajidConversations: Record<"en" | "ar", Conversation[]> = {
  en: [
    {
      question: "Who are my biggest customers at risk of churning?",
      response: "Here are your top 3 customers at risk based on their purchase patterns:",
      type: "churn",
      customers: [
        { name: "Al Noor Trading Est.", value: "45,200 SAR over 6 months", risk: 87 },
        { name: "Al Wafa Trading Co.", value: "38,750 SAR over 6 months", risk: 72 },
        { name: "Abdullah Al-Mutairi", value: "29,300 SAR over 6 months", risk: 65 },
      ],
      total: "113,250 SAR",
      advice: "Send them a special offer or exclusive coupon now before they go to a competitor!",
    },
    {
      question: "If I do one thing this month to increase sales, what should it be?",
      response: "Your biggest missed opportunity is cancelled COD orders:",
      type: "cod",
      alert: "High-value COD orders being cancelled!",
      subtitle: "23% of Cash on Delivery orders are cancelled",
      lost: "47,800 SAR",
      recoverable: "28,600 SAR",
      advice: "Enable WhatsApp order confirmation before shipping - reduces cancellations by 60%",
    },
    {
      question: "Give me a complete report about last month",
      response: "Here's your store performance summary for December:",
      type: "dashboard",
      period: "December 2024",
      badge: "🔥 Best month!",
      sales: "284,650 SAR",
      salesLabel: "Total Sales",
      metrics: [
        { label: "Orders", value: "847", change: "+12%" },
        { label: "Avg Order", value: "336 SAR", change: "+5%" },
        { label: "Products Sold", value: "1,284", change: "+15%" },
        { label: "Net Profit", value: "52,400 SAR", change: "+22%" },
      ],
    },
  ],
  ar: [
    {
      question: "من هم اكبر العملاء الي على وشك خسارتهم؟",
      response: "هؤلاء أكبر 3 عملاء معرضين للخسارة بناءً على نمط شرائهم:",
      type: "churn",
      customers: [
        { name: "مؤسسة النور للتجارة", value: "45,200 ر.س خلال 6 أشهر", risk: 87 },
        { name: "شركة الوفاء التجارية", value: "38,750 ر.س خلال 6 أشهر", risk: 72 },
        { name: "عبدالله المطيري", value: "29,300 ر.س خلال 6 أشهر", risk: 65 },
      ],
      total: "113,250 ر.س",
      advice: "أرسل لهم عرض خاص أو كوبون حصري الحين قبل ما يروحون للمنافس!",
    },
    {
      question: "لو بسوي شي واحد هالشهر عشان أزيد المبيعات، وش بيكون؟",
      response: "أكبر فرصة ضائعة عندك هي طلبات الدفع عند الاستلام الملغية:",
      type: "cod",
      alert: "طلبات COD ملغية بقيمة عالية!",
      subtitle: "23% من طلبات الدفع عند الاستلام تُلغى",
      lost: "47,800 ر.س",
      recoverable: "28,600 ر.س",
      advice: "فعّل تأكيد الطلب عبر واتساب قبل الشحن - يقلل الإلغاء بنسبة 60%",
    },
    {
      question: "عطني تقرير كامل شامل عن الشهر الماضي",
      response: "إليك ملخص أداء متجرك لشهر ديسمبر:",
      type: "dashboard",
      period: "ديسمبر 2025",
      badge: "🔥 أفضل شهر!",
      sales: "284,650 ر.س",
      salesLabel: "إجمالي المبيعات",
      metrics: [
        { label: "الطلبات", value: "847", change: "+12%" },
        { label: "متوسط الطلب", value: "336 ر.س", change: "+5%" },
        { label: "المنتجات المباعة", value: "1,284", change: "+15%" },
        { label: "صافي الربح", value: "52,400 ر.س", change: "+22%" },
      ],
    },
  ],
}

const ibnMajidAvatar =
  "https://bousla.com/assets/ibn_majed_avatar-234fd9bfcb41df29ef1b2eca14ae766535ca013450c388516625031962fd5a3f.png"

function ChurnResult({ conversation, locale }: { conversation: ChurnConversation; locale: "en" | "ar" }) {
  return (
    <div className="ibn-result ibn-result--churn">
      <div className="ibn-customer-list">
        {conversation.customers.map((customer, index) => (
          <div className="ibn-customer" key={customer.name}>
            <span className="ibn-customer__rank">{index + 1}</span>
            <span className="ibn-customer__name">
              <strong>{customer.name}</strong>
              <small>{customer.value}</small>
            </span>
            <span className="ibn-customer__risk">
              <i><b style={{ width: `${customer.risk}%` }} /></i>
              <strong>{customer.risk}%</strong>
              <small>{locale === "ar" ? "خطر المغادرة" : "Churn Risk"}</small>
            </span>
          </div>
        ))}
      </div>
      <div className="ibn-total">
        <span>{locale === "ar" ? "إجمالي القيمة المعرضة للخطر" : "Total Value at Risk"}</span>
        <strong>{conversation.total}</strong>
      </div>
      <div className="ibn-advice"><Lightbulb aria-hidden="true" /><span>{conversation.advice}</span></div>
    </div>
  )
}

function CodResult({ conversation, locale }: { conversation: CodConversation; locale: "en" | "ar" }) {
  return (
    <div className="ibn-result ibn-result--cod">
      <div className="ibn-alert">
        <span aria-hidden="true">!</span>
        <div><strong>{conversation.alert}</strong><small>{conversation.subtitle}</small></div>
      </div>
      <div className="ibn-stats">
        <div><strong>{conversation.lost}</strong><small>{locale === "ar" ? "إيرادات مفقودة" : "Lost Rev."}</small></div>
        <div><strong>{conversation.recoverable}</strong><small>{locale === "ar" ? "قابلة للاسترداد" : "Recoverable"}</small></div>
      </div>
      <div className="ibn-advice ibn-advice--orange"><Lightbulb aria-hidden="true" /><span>{conversation.advice}</span></div>
    </div>
  )
}

function DashboardResult({ conversation, locale }: { conversation: DashboardConversation; locale: "en" | "ar" }) {
  const bars = [35, 42, 38, 51, 45, 62, 58, 65, 52, 48, 55, 68, 72, 61, 59, 75, 69, 82, 78, 66, 73, 81, 77, 85, 79, 76, 83, 87, 84, 90]

  return (
    <div className="ibn-result ibn-result--dashboard">
      <div className="ibn-dashboard__header"><strong>{conversation.period}</strong><span>{conversation.badge}</span></div>
      <div className="ibn-dashboard__sales">
        <span><small>{conversation.salesLabel}</small><strong>{conversation.sales}</strong></span>
        <b>↑ +18%</b>
      </div>
      <div className="ibn-dashboard__metrics">
        {conversation.metrics.map((metric) => (
          <span key={metric.label}><strong>{metric.value}</strong><small>{metric.label}</small><b>↑ {metric.change}</b></span>
        ))}
      </div>
      <div className="ibn-dashboard__sparkline">
        <span>{locale === "ar" ? "المبيعات اليومية" : "Daily Sales"}</span>
        <b>{locale === "ar" ? "في ارتفاع ↑" : "Trending up ↑"}</b>
        <i>{bars.map((height, index) => <b key={index} style={{ height: `${height}%` }} />)}</i>
      </div>
    </div>
  )
}

export function BouslaIbnMajidPreview() {
  const { locale } = usePreferences()
  const conversations = ibnMajidConversations[locale]
  const [activeConversation, setActiveConversation] = useState({ index: 0, run: 0 })
  const [step, setStep] = useState<0 | 1 | 2>(0)
  const conversation = conversations[activeConversation.index]

  useEffect(() => {
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    const timers: number[] = []

    timers.push(window.setTimeout(() => setStep(reducedMotion ? 2 : 0), 0))

    if (!reducedMotion) {
      timers.push(window.setTimeout(() => setStep(1), 500))
      timers.push(window.setTimeout(() => setStep(2), 2_000))
      timers.push(
        window.setTimeout(
          () => setActiveConversation(({ index, run }) => ({
            index: (index + 1) % conversations.length,
            run: run + 1,
          })),
          9_000,
        ),
      )
    }

    return () => timers.forEach((timer) => window.clearTimeout(timer))
  }, [activeConversation, conversations.length, locale])

  return (
    <div className="bousla-visual ibn-majid" aria-label={locale === "ar" ? "عرض ابن ماجد التفاعلي" : "Ibn Majid interactive demo"}>
      <header className="ibn-majid__header">
        <span className="ibn-avatar ibn-avatar--large"><img src={ibnMajidAvatar} alt="" /><i /></span>
        <strong>{locale === "ar" ? "ابن ماجد" : "Ibn Majid"}</strong>
      </header>

      <div className="ibn-majid__messages" aria-live="polite">
        {step >= 1 && <div className="ibn-user-message">{conversation.question}</div>}
        {step === 1 && (
          <div className="ibn-response-row">
            <span className="ibn-avatar"><img src={ibnMajidAvatar} alt="" /></span>
            <span className="ibn-typing"><i /><i /><i /></span>
          </div>
        )}
        {step >= 2 && (
          <div className="ibn-response-row">
            <span className="ibn-avatar"><img src={ibnMajidAvatar} alt="" /></span>
            <div className="ibn-response">
              <p>{conversation.response}</p>
              {conversation.type === "churn" && <ChurnResult conversation={conversation} locale={locale} />}
              {conversation.type === "cod" && <CodResult conversation={conversation} locale={locale} />}
              {conversation.type === "dashboard" && <DashboardResult conversation={conversation} locale={locale} />}
            </div>
          </div>
        )}
      </div>

      <div className="ibn-majid__questions">
        <span>{locale === "ar" ? "جرّب أن تسأل:" : "Try asking:"}</span>
        <div>
          {conversations.map((item, index) => (
            <button
              type="button"
              onClick={() => setActiveConversation(({ run }) => ({ index, run: run + 1 }))}
              aria-pressed={activeConversation.index === index}
              key={item.question}
            >
              {item.question}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
